/*
 * aiClerk 当前处理的文件上下文
 *
 * 为什么要 context：
 *   - 之前用 URL query 传 fileUrl/fileName，地址栏长、且不适合分享
 *   - 刷新兜底：写 sessionStorage，刷新 work 页能恢复
 *   - 需要分享会话时走 ?sessionId=xxx 这种语义清晰的 query，不污染它
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

const STORAGE_KEY = 'aiClerk:currentFile'

export interface CurrentFile {
  fileUrl: string
  fileName: string
  /** 后端 LLM 容器内的本地路径（如 "uploads/xxx.xlsx"），首条消息会注入到 prompt
   *  让 AI 直接 read 而不是 download_oss。后续轮次不再注入。 */
  filePath?: string
  /** 左侧预览专用 URL（上传响应里的 refreshUrl）。点"刷新"按钮时拼 t=<rand>
   *  做缓存破坏，强制重新拉。fileUrl 仍用于发给 AI 做工作副本。 */
  refreshUrl?: string
  /** 工作副本 id, 用于 PATCH /platform/files/{workingId} 重命名。
   *  只有走 upload 新建/上传的文档有;老列表(listRecentFiles)回来的文件没有,重命名按钮降级。 */
  workingId?: string
}

interface FileCtxValue {
  file: CurrentFile | null
  setFile: (f: CurrentFile | null) => void
  /** 增量 patch 当前文件的字段(重命名后只刷 fileName,不动 fileUrl/filePath/refreshUrl)。
   *  path 不变 → 后端 signed URL 也不变,只需要更新界面展示字段。 */
  patchFile: (partial: Partial<CurrentFile>) => void
}

const FileCtx = createContext<FileCtxValue>({
  file: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFile: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  patchFile: () => {}
})

function readStorage(): CurrentFile | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CurrentFile
    if (parsed && typeof parsed.fileUrl === 'string' && parsed.fileUrl) {
      return parsed
    }
  } catch {
    /* no-op */
  }
  return null
}

function writeStorage(f: CurrentFile | null) {
  try {
    if (f) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f))
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* no-op */
  }
}

export const FileProvider: React.FC<{ children?: React.ReactNode }> = ({
  children
}) => {
  const [file, setFileState] = useState<CurrentFile | null>(() => readStorage())

  const setFile = useCallback((f: CurrentFile | null) => {
    setFileState(f)
    writeStorage(f)
  }, [])

  const patchFile = useCallback((partial: Partial<CurrentFile>) => {
    setFileState((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...partial }
      writeStorage(next)
      return next
    })
  }, [])

  // 同 tab 多窗口 / 其他代码写 sessionStorage 时同步
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      setFileState(readStorage())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo<FileCtxValue>(
    () => ({ file, setFile, patchFile }),
    [file, setFile, patchFile]
  )

  return <FileCtx.Provider value={value}>{children}</FileCtx.Provider>
}

export function useCurrentFile(): FileCtxValue {
  return useContext(FileCtx)
}
